/**
 * Confirm it's you — `docs/rowted-admin-v2.md` §4.3, board 4.
 *
 * One dialog, used by every sensitive button in Rowted Admin. Before this,
 * three screens each had their own inline "six-digit code" box beside a Save
 * button, and each behaved slightly differently: one cleared the code on
 * failure, one did not, none of them replayed the thing you had been trying to
 * do. §4.3 asks for one component, so this is it.
 *
 * **The request is replayed, not re-triggered.** `guard(fn)` runs `fn`, and if
 * the server answers `step_up_required` it holds `fn` aside, asks for the code,
 * posts it to `/auth/step-up`, and then runs `fn` again. The caller writes the
 * action once and never mentions step-up; a person types a code and their click
 * happens, rather than being told to click it again.
 *
 * A recovery code is the same field: six digits or ten characters, the server
 * knows which, and `POST /auth/step-up` takes either. So "Use a recovery code"
 * changes the label and the box rather than the endpoint.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import { Button } from '../../components/primitives.js'
import { Modal, STEP_UP_LAYER } from '../../components/overlays.js'
import { CodeInput } from '../../components/code-input.js'

export interface StepUp {
  /**
   * Run something that may need a code first.
   *
   * Resolves true when it ran, false when the person closed the dialog. Any
   * other failure is thrown, because it is the caller's to report — this hook
   * knows about one error code and nothing else.
   */
  guard: (action: () => Promise<void>) => Promise<boolean>
  /** Render this once, anywhere in the page. */
  dialog: ReactNode
}

export function useStepUp(): StepUp {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /* The action waiting on a code, and who to tell once it has run. */
  const held = useRef<{
    action: () => Promise<void>
    settle: (ran: boolean) => void
    fail: (error: unknown) => void
  } | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    setCode('')
    setRecovery(false)
    setError(null)
    held.current?.settle(false)
    held.current = null
  }, [])

  const guard = useCallback((action: () => Promise<void>) =>
    new Promise<boolean>((settle, fail) => {
      action().then(() => settle(true)).catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.code === 'step_up_required') {
          held.current = { action, settle, fail }
          setError(null)
          setCode('')
          setOpen(true)
          return
        }
        fail(caught)
      })
    }), [])

  /*
   * `typed` rather than `code`, because of when this is called.
   *
   * `CodeInput` fires `onComplete` in the same handler that calls `onChange`,
   * so at that instant React has not applied the new state yet and `code` is
   * still the *first five* digits. Reading it here sent five characters to a
   * route that wants six, and the screen said "String must contain at least 6
   * character(s)" at somebody who had typed six — with no way to get past it,
   * because the same thing happened on every attempt. The argument carries
   * what was actually typed; the state is right by the time Continue is
   * pressed, which is why the button path never showed this.
   */
  const confirm = useCallback(async (typed?: string) => {
    const waiting = held.current
    if (!waiting || busy) return
    const entered = (typed ?? code).trim()
    setBusy(true)
    setError(null)
    try {
      await adminRequest('/auth/step-up', { method: 'POST', body: { code: entered } })
    } catch (caught) {
      setBusy(false)
      setCode('')
      setError(caught instanceof ApiError ? caught.message : 'That code did not work.')
      return
    }
    /*
     * The replay's own failure belongs to the caller, not to this dialog: the
     * code was right, and "the role could not be changed" is not a code
     * problem. So the dialog closes either way and the promise carries it.
     */
    held.current = null
    setBusy(false)
    setOpen(false)
    setCode('')
    setRecovery(false)
    try {
      await waiting.action()
      waiting.settle(true)
    } catch (caught) {
      waiting.fail(caught)
    }
  }, [busy, code])

  const ready = recovery ? code.trim().length >= 8 : code.trim().length === 6

  const dialog = (
    <Modal
      open={open}
      onClose={close}
      /*
       * Above everything, because this is the one dialog that can be raised
       * from inside anything: the integration key flyouts sit at 70, and a
       * step-up at the default 50 drew behind them. Not merely hard to read —
       * clicking a code box landed on the flyout's scrim, which dismissed this
       * dialog and abandoned the save, so the keys could not be saved at all.
       */
      layer={STEP_UP_LAYER}
      title="Confirm it’s you"
      subtitle={recovery
        ? 'Type one of the recovery codes you saved when you enrolled.'
        : 'Type the six-digit code from your authenticator.'}
      width={420}
      footer={(
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => void confirm()}>
            Continue
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        {recovery
          ? (
            <input
              aria-label="Recovery code"
              value={code}
              autoFocus
              onChange={(event) => setCode(event.target.value)}
              className="h-11 w-full rounded-field border border-line bg-field px-3 font-mono
                         text-[15px] tracking-[0.08em] text-text outline-none
                         focus:border-accent-line focus:ring-2 focus:ring-accent-ring"
            />
          )
          : (
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={(typed) => void confirm(typed)}
              disabled={busy}
              autoFocus
              label="Sign-in code"
            />
          )}

        {error && (
          <p role="alert" className="m-0 text-cell text-danger">{error}</p>
        )}

        <button
          type="button"
          onClick={() => { setRecovery(!recovery); setCode(''); setError(null) }}
          className="self-start text-cell font-semibold text-accent hover:text-accent-dark"
        >
          {recovery ? 'Use my authenticator' : 'Use a recovery code'}
        </button>
      </div>
    </Modal>
  )

  return { guard, dialog }
}
